import type { CSSProperties } from 'react';
import { T, WEDGE_BRIEF_CLOSE } from '@territory/tokens';
import { radius } from '../scale.js';
import { focusRing } from './focusRing.js';
import { wedgeExactRole } from './wedgeRoles.js';

/*
 * The Wedge Brief V2 icon button (apps/wedge/src/prototype/prep-brief-v2-kit.jsx Bv2IconButton), for IconButton
 * variant="wedge-bv2". Verbatim from the prototype so the swap is a visual no-op: box max(size, 40) with a 44px floor,
 * radius 12, 1px line border, the UA button padding (the prototype never resets it), card fill, text ink, hover and pressed accent-soft fill, hover lift 1px, 180ms ease.
 * The prototype has no focus ring or disabled look; those use the one core focus ring and the core disabled opacity.
 * Roles read the --wedge-bv2-* roles in products.wedge; outside Wedge each falls back to a base token.
 */

/* Roles the button reads. products.wedge.roles must set each in both themes. */
export const WEDGE_ICON_BUTTON_ROLES = ['card', 'line', 'text', 'acc-soft', 'acc-ink'] as const;
export type WedgeIconButtonRole = (typeof WEDGE_ICON_BUTTON_ROLES)[number];

const role = (name: WedgeIconButtonRole, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;

/* verbatim Bv2IconButton, off-scale */
export const WEDGE_ICON_BUTTON_DEFAULT = 40;
export const WEDGE_ICON_BUTTON_MIN = 44;
export const WEDGE_ICON_BUTTON_GLYPH = 18;
const RADIUS = 12;
const LIFT_PX = 1;
const MS = 180;
/* BV2_EASE */
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const DISABLED_OPACITY = 0.4;

const SEL = '[data-rv-icon-button][data-rv-variant="wedge-bv2"]';

/* The last rule beats the shell's .wedge-scope button:focus-visible 8px radius, which the prototype's inline radius out-ranks. */
export const WEDGE_ICON_BUTTON_CSS = `${SEL} { box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; margin: 0; border-radius: ${RADIUS}px; border: 1px solid ${role('line', T.separator)}; background: ${role('card', T.surface)}; color: ${role('text', T.label)}; cursor: pointer; transform: none; transition: transform ${MS}ms ${EASE}, background ${MS}ms ${EASE}; }
${SEL}[aria-pressed="true"] { background: ${role('acc-soft', T.accentSoft)}; color: ${role('acc-ink', T.accent)}; }
${SEL}:not([disabled], [aria-disabled="true"]):hover { background: ${role('acc-soft', T.accentSoft)}; transform: translateY(-${LIFT_PX}px); }
${SEL}[disabled], ${SEL}[aria-disabled="true"] { opacity: ${DISABLED_OPACITY}; cursor: not-allowed; }
@media (prefers-reduced-motion: reduce) { ${SEL} { transition: none; } ${SEL}:not([disabled], [aria-disabled="true"]):hover { transform: none; } }
${focusRing(SEL)}
${SEL}:focus-visible { border-radius: ${RADIUS}px; }`;

/*
 * The Wedge daily digest close button (apps/wedge/src/prototype/prep-screens-3.jsx DailyDigest), for IconButton
 * variant="wedge-digest-close". Verbatim from the prototype so the swap is a visual no-op: one bare 44px square (11px
 * padding round a 22px glyph), no fill, no border, secondary ink. The prototype draws no hover or press look of its own,
 * so this look has none; keyboard focus gets the shipped Wedge ring and the core ring, which repeat each other.
 * The ink keeps the legacy variable text, so a shipped ink rule that matches it still applies.
 */
/* verbatim DailyDigest close button, off-scale */
export const WEDGE_DIGEST_CLOSE_GLYPH = 22;
const DIGEST_CLOSE_PADDING = 11;

export const wedgeDigestCloseStyle = (): CSSProperties => ({
  background: 'none',
  border: 0,
  color: wedgeExactRole('labelSec'),
  padding: DIGEST_CLOSE_PADDING,
  cursor: 'pointer',
  minWidth: WEDGE_ICON_BUTTON_MIN,
  minHeight: WEDGE_ICON_BUTTON_MIN,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/*
 * The Wedge field card close button (apps/wedge/src/prototype/prep-screens-2.jsx FieldCardModal), for IconButton
 * variant="wedge-field-close". Verbatim from the prototype so the swap is a visual no-op: one 34px circle on the surface fill with a
 * 1px card border and the label ink, a 16px glyph drawn directly in it, and no padding. The prototype draws no hover or press look
 * of its own beyond the shipped wedge-hoverable class, which the caller keeps. Each role keeps the exact legacy variable text, so a
 * shipped ink rule that matches it still applies.
 * The prototype target is the 34px circle. This look adds a transparent 44px square centered on it. The circle sits in the corner of
 * a card with 22px of padding and 14px of space under its row, so the region grows only into empty card surface and takes no tap from
 * another control. The region is anchored on a relative, isolated button and sits below the glyph.
 */
/* verbatim FieldCardModal close button, off-scale */
export const WEDGE_FIELD_CLOSE_BOX = 34;
export const WEDGE_FIELD_CLOSE_GLYPH = 16;

const FIELD_CLOSE_SEL = '[data-rv-icon-button][data-rv-variant="wedge-field-close"]';
/* The region inherits `pointer-events`, so a closed card (opacity 0, `pointer-events: none`) leaves no clear target behind. Same
   reason as the Wedge settings hit sheet in wedgeSettingsButtonHit.ts. */
export const WEDGE_FIELD_CLOSE_CSS = `${FIELD_CLOSE_SEL}::before { content: ''; position: absolute; left: 50%; top: 50%; width: ${WEDGE_ICON_BUTTON_MIN}px; height: ${WEDGE_ICON_BUTTON_MIN}px; transform: translate(-50%, -50%); z-index: -1; background: transparent; border: 0; padding: 0; pointer-events: inherit; }`;

export const wedgeFieldCloseStyle = (): CSSProperties => ({
  position: 'relative',
  isolation: 'isolate',
  width: WEDGE_FIELD_CLOSE_BOX,
  height: WEDGE_FIELD_CLOSE_BOX,
  borderRadius: radius.pill,
  background: wedgeExactRole('surface'),
  border: `1px solid ${wedgeExactRole('cardBorder')}`,
  color: wedgeExactRole('label'),
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
});

/* The Phone brief close keeps its exact 40px target and shipped focus behavior. */
const BRIEF_CLOSE_BOX = 40;
export const WEDGE_BRIEF_CLOSE_GLYPH = 18;
export const wedgeBriefCloseStyle = (): CSSProperties => ({
  width: BRIEF_CLOSE_BOX, height: BRIEF_CLOSE_BOX, borderRadius: radius.pill,
  background: wedgeExactRole('surface'), border: `1px solid ${wedgeExactRole('cardBorder')}`,
  color: wedgeExactRole('label'), cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 0, boxShadow: WEDGE_BRIEF_CLOSE.shadow,
  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
});
